import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { portalKeys } from "@/client/lib/query";
import { usePortalPreflight, usePortalBootstrap, formatDutchDate } from "@/client/lib/portal";
import { PortalNav } from "@/client/components/school-portal/PortalNav";
import { PortalHeader } from "@/client/components/school-portal/PortalHeader";
import { Button } from "@/client/components/ui/button";
import { CheckIcon, PencilIcon, TriangleAlert, InfoIcon } from "lucide-react";

// ─── Types from /overview endpoint ───

type FlowState = { lastSubmittedAt: string | null; hasUnsubmittedChanges: boolean; everSubmitted: boolean; dependencyChanged: boolean };
type AvailabilityCategory = { key: string; label: string; state: "yes" | "no" | "later" | "geen" | "empty"; entries: string[] };
type ProgrammaItem = { title: string; provider: string | null; groups: string; status: string | null };
type ProgrammaSection = { key: string; label: string; items: ProgrammaItem[] };

type OverviewData = {
  school: { schoolId: number; schoolName: string; shortName: string | null };
  phase: "data" | "waiting" | "selection" | "complete";
  stats: { contacts: number; groups: number; totalStudents: number; constraints: number };
  contacts: Array<{ personId: number; role: string; name: string; emails: string[]; phones: string[] }>;
  groups: Array<{ id: number; displayName: string; yearLevels: Array<{ code: string; label: string; studentCount: number }>; totalStudents: number; vrijeDagdelenLater: boolean; vrijeDagdelenGeen: boolean; vakdocentLater: boolean; vakdocentGeen: boolean }>;
  availability: AvailabilityCategory[];
  programma: { sections: ProgrammaSection[]; hasAny: boolean };
  flows: { schoolgegevens: FlowState; programmaselectie: FlowState };
  // Authoritative programma completeness (same check that gates aanleveren). A delivered
  // programma can still be INCOMPLETE (e.g. a group with vast aanbod left undecided), so the
  // journey step reads "Nog niet compleet" with attention rather than "Gekozen".
  programmaComplete: boolean;
};

export function SchoolOverviewPage() {
  const { schoolId: scIdParam } = useParams<{ schoolId: string }>();
  const schoolId = Number(scIdParam);
  usePortalPreflight(schoolId);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: portalKeys.overview(schoolId),
    queryFn: async (): Promise<OverviewData> => {
      const res = await fetch(`/api/school/${schoolId}/overview`, { credentials: "include" });
      if (!res.ok) throw new Error("overview_failed");
      return res.json();
    },
    enabled: Number.isFinite(schoolId),
  });

  // The shell (header + nav tabs) renders immediately and stays put; only the body
  // shows a skeleton or cached content. No full-screen "Laden..." reset on navigation.
  return (
    <div className="min-h-screen bg-background">
      <PortalNav schoolName={data?.school.schoolName} schoolId={schoolId} phase={data?.phase} />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {isPending ? (
          <OverviewSkeleton />
        ) : isError || !data ? (
          <div className="text-center">
            <h1 className="text-lg font-semibold text-foreground">Kan de gegevens niet laden</h1>
            <p className="mt-2 text-sm text-muted-foreground">Controleer uw internetverbinding en probeer het opnieuw.</p>
            <Button className="mt-4" onClick={() => refetch()}>Opnieuw proberen</Button>
          </div>
        ) : (
          <OverviewBody data={data} schoolId={schoolId} />
        )}
      </main>
    </div>
  );
}

function OverviewBody({ data, schoolId }: { data: OverviewData; schoolId: number }) {
  // The app-wide planning deadline, from the shared portal bootstrap (cached). Shown on the
  // journey strip so the Overzicht stays in sync with the Controleren/aanlever copy.
  const { data: bootstrap } = usePortalBootstrap(schoolId);
  const planningDeadline = bootstrap?.planningDeadline ?? null;
  const planningStarted = bootstrap?.planningStarted ?? false;
  return (
    <>
      <PortalHeader title="Overzicht" subtitle="Bekijk wat u heeft ingevuld en wat er nog moet gebeuren." />

      <div className="space-y-10">
        <OverviewPhaseIndicator data={data} schoolId={schoolId} planningDeadline={planningDeadline} planningStarted={planningStarted} />
        <ReceivedSummary data={data} schoolId={schoolId} />
      </div>
    </>
  );
}

// One calm bordered panel with four digest rows. Each row reads like a short summary
// (a strong takeaway line + a muted detail line), not a label/value/action table row.
// The Overzicht confirms what KUVO received and points into the detail pages; it is not
// a data export. Full detail lives under Gegevens/Programma, one click away via "Wijzigen".
function ReceivedSummary({ data, schoolId }: { data: OverviewData; schoolId: number }) {
  return (
    <section aria-labelledby="ontvangen-title">
      <h2 id="ontvangen-title" className="text-base font-semibold text-foreground">Ontvangen gegevens</h2>
      <p className="mt-1 text-sm text-muted-foreground">Klopt er iets niet? Kies Wijzigen om het aan te passen.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <SummaryRow title="Contactgegevens" href={`/school/${schoolId}/schoolgegevens/contactgegevens`}>
          <ContactsSummary contacts={data.contacts} />
        </SummaryRow>
        <SummaryRow title="Groepen" href={`/school/${schoolId}/schoolgegevens/groepen`}>
          <GroupsSummary groups={data.groups} total={data.stats.totalStudents} />
        </SummaryRow>
        <SummaryRow title="Beschikbaarheid" href={`/school/${schoolId}/schoolgegevens/studiedagen`}>
          <AvailabilitySummary cats={data.availability} groups={data.groups} />
        </SummaryRow>
        <SummaryRow title="Programma" href={`/school/${schoolId}/programmaselectie`}>
          <ProgrammaSummary programma={data.programma} />
        </SummaryRow>
      </div>
    </section>
  );
}

function SummaryRow({ title, href, children }: { title: string; href: string; children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col rounded-lg border bg-card p-5 shadow-xs">
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-[15px] font-semibold leading-6 text-foreground">{title}</h3>
        <Link
          to={href}
          aria-label={`${title} wijzigen`}
          className="shrink-0 text-sm font-medium leading-6 text-primary hover:underline"
        >
          Wijzigen
        </Link>
      </div>
      <div className="mt-2 space-y-0.5 text-sm leading-6 text-muted-foreground">{children}</div>
    </div>
  );
}

/** The strong "takeaway" first line of a row, distinct from the muted detail below it. */
function Lead({ children }: { children: React.ReactNode }) {
  return <p className="font-medium text-foreground">{children}</p>;
}

function ContactsSummary({ contacts }: { contacts: OverviewData["contacts"] }) {
  if (contacts.length === 0) return <p>Nog geen contactpersonen ingevuld.</p>;
  // Role-first lines (coordinator first). Only these roles are collected, so naming them
  // reads clearer than a vague count ("2 contactpersonen").
  const rank = (r: string) => (/co[oö]rdinator|kunst|cultuur/i.test(r) ? 0 : /directeur/i.test(r) ? 1 : 2);
  const ordered = [...contacts].sort((a, b) => rank(a.role) - rank(b.role));
  return (
    <div className="space-y-2">
      {ordered.map((c) => (
        <div key={`${c.personId}-${c.role}`}>
          <p className="font-medium text-foreground">{c.role}</p>
          <p className="text-muted-foreground">{c.name}{c.emails[0] ? `, ${c.emails[0]}` : ""}</p>
        </div>
      ))}
    </div>
  );
}

function GroupsSummary({ groups, total }: { groups: OverviewData["groups"]; total: number }) {
  if (groups.length === 0) return <p>Nog geen groepen ingevuld.</p>;
  const shown = groups.slice(0, 6);
  const rest = groups.length - shown.length;
  return (
    <>
      <Lead>{groups.length} {groups.length === 1 ? "groep" : "groepen"}, {total} leerlingen</Lead>
      <p>
        {shown.map((g) => `${g.displayName} (${g.totalStudents})`).join(", ")}
        {rest > 0 ? `, en ${rest} meer` : ""}
      </p>
    </>
  );
}

const AVAIL_NOUN: Record<string, [string, string]> = {
  studiedagen: ["studiedag", "studiedagen"],
  dagdelen: ["vrij dagdeel", "vrije dagdelen"],
  vakdocenten: ["vakdocent", "vakdocenten"],
};

export function buildAvailabilityLines(
  cats: AvailabilityCategory[],
  groups: OverviewData["groups"],
): { text: string; amber?: boolean }[] {
  const lines: { text: string; amber?: boolean }[] = [];

  // Studiedagen is school-wide (one answer), so keep a short status line from the category.
  const studiedagen = cats.find((c) => c.key === "studiedagen");
  if (studiedagen) {
    const [sg, pl] = AVAIL_NOUN.studiedagen!;
    if (studiedagen.state === "yes") {
      const n = studiedagen.entries.length;
      lines.push({ text: `Studiedagen: ${n} ${n === 1 ? sg : pl} opgegeven.` });
    } else if (studiedagen.state === "later") {
      lines.push({ text: "Studiedagen: worden later ingevuld.", amber: true });
    } else if (studiedagen.state === "no" || studiedagen.state === "geen") {
      lines.push({ text: "Studiedagen: geen opgegeven." });
    }
  }

  // The two per-group types: a compact count of how the groups answered, instead of a per-group
  // list. Every group is in exactly one bucket (opgeven / geen / later), matching the group
  // dialog's 3-way choice. A group with neither _geen nor _later set counts as "opgegeven".
  const n = groups.length;
  if (n > 0) {
    const perGroup: { key: "dagdelen" | "vakdocenten"; label: string; later: (g: OverviewData["groups"][number]) => boolean; geen: (g: OverviewData["groups"][number]) => boolean }[] = [
      { key: "dagdelen", label: "Vrije dagdelen", later: (g) => g.vrijeDagdelenLater, geen: (g) => g.vrijeDagdelenGeen },
      { key: "vakdocenten", label: "Vakdocenten", later: (g) => g.vakdocentLater, geen: (g) => g.vakdocentGeen },
    ];
    for (const t of perGroup) {
      const later = groups.filter(t.later).length;
      const geen = groups.filter(t.geen).length;
      const opgegeven = n - later - geen;
      const parts = [`${opgegeven} opgegeven`, `${geen} geen`, `${later} later`];
      lines.push({ text: `${t.label}: ${parts.join(", ")} (van ${n} ${n === 1 ? "groep" : "groepen"}).`, amber: later > 0 });
    }
  }

  return lines;
}

/**
 * Render the beschikbaarheid summary lines as clean Origin UI inline alerts. Shared by the
 * Overzicht (AvailabilitySummary) and Controleren so both read identically: an amber warning
 * alert when something is still pending ("later"), otherwise a neutral blue info alert. Each
 * line is one compact bordered row with the icon aligned to the first line of text.
 */
export function AvailabilityAlerts({
  lines,
}: {
  lines: { text: string; amber?: boolean }[];
}) {
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <div key={i} className="rounded-md border px-4 py-3">
          <p className="text-sm">
            {l.amber ? (
              <TriangleAlert aria-hidden className="-mt-0.5 me-3 inline-flex text-amber-500" size={16} />
            ) : (
              <InfoIcon aria-hidden className="-mt-0.5 me-3 inline-flex text-blue-500" size={16} />
            )}
            {l.text}
          </p>
        </div>
      ))}
    </div>
  );
}

function AvailabilitySummary({ cats, groups }: { cats: AvailabilityCategory[]; groups: OverviewData["groups"] }) {
  // Compact count summary: one status line for the school-wide Studiedagen, plus one count line
  // per per-group type ("X opgegeven, Y geen, Z later (van N groepen)"). No per-group list.
  const lines = buildAvailabilityLines(cats, groups);
  if (lines.length === 0) return <p className="text-muted-foreground/70">Nog niet ingevuld.</p>;
  return <AvailabilityAlerts lines={lines} />;
}

/** Programme titles sometimes already carry their category as a prefix (e.g. the TechnoHub
 *  offering "TechnoHub: Robotica"). Strip a leading category-label prefix so the digest does
 *  not read "TechnoHub: TechnoHub Robotica". */
function cleanProgTitle(title: string, sectionLabel: string): string {
  const t = title.trim();
  if (t.toLowerCase().startsWith(sectionLabel.toLowerCase())) {
    const rest = t.slice(sectionLabel.length).replace(/^[\s:]+/, "");
    if (rest) return rest;
  }
  return t;
}

function ProgrammaSummary({ programma }: { programma: OverviewData["programma"] }) {
  if (!programma.hasAny) return <p>Nog geen programma gekozen.</p>;
  const items = programma.sections.flatMap((s) => s.items.map((it) => cleanProgTitle(it.title, s.label)));
  const confirmed = programma.sections.flatMap((s) => s.items).filter((it) => it.status === "Bevestigd").length;
  const shown = items.slice(0, 5);
  const rest = items.length - shown.length;
  return (
    <>
      <Lead>
        {items.length} {items.length === 1 ? "keuze" : "keuzes"}{confirmed > 0 ? `, waarvan ${confirmed} bevestigd` : ""}
      </Lead>
      <p>
        {shown.join(", ")}
        {rest > 0 ? `, en ${rest} meer` : ""}
      </p>
    </>
  );
}

function OverviewSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="space-y-2">
        <div className="h-6 w-40 rounded bg-muted" />
        <div className="h-4 w-72 max-w-full rounded bg-muted" />
      </div>
      <div className="h-28 rounded-lg bg-muted" />
      <div className="h-64 rounded-lg bg-muted" />
    </div>
  );
}

// The overall journey, shown as one compact horizontal strip at the top of Overzicht so a
// non-technical user sees at a glance where they are and what is next. The current step is
// derived from the flows (unambiguous), not the server's overloaded `phase` name.
const JOURNEY = [
  { key: "schoolgegevens", label: "Schoolgegevens aanleveren" },
  { key: "programmaselectie", label: "Programma kiezen" },
  { key: "planning", label: "KUVO begint met de planning" },
  { key: "definitief", label: "Planning najaar definitief" },
] as const;

function OverviewPhaseIndicator({ data, schoolId, planningDeadline, planningStarted }: { data: OverviewData; schoolId: number; planningDeadline: string | null; planningStarted: boolean }) {
  const g = data.flows.schoolgegevens;
  const p = data.flows.programmaselectie;
  const gegevensIn = g.everSubmitted;
  // A delivered programma that is NOT complete (a group with vast aanbod left undecided, e.g.
  // submitted before that rule existed) must NOT count as a finished step: it needs attention
  // just like a pending change does. So "programmaIn" requires delivered AND complete.
  const programmaIncomplete = p.everSubmitted && !data.programmaComplete;
  const programmaIn = p.everSubmitted && data.programmaComplete;
  const gegevensAttention = g.everSubmitted && g.hasUnsubmittedChanges;
  // Attention when there are pending changes OR the delivered programma is still incomplete.
  const programmaAttention = p.everSubmitted && (p.hasUnsubmittedChanges || programmaIncomplete);

  // First step not yet handed in is "current"; once both are in, KUVO is planning (step 2).
  const currentIndex = !gegevensIn ? 0 : !programmaIn ? 1 : 2;

  // The "KUVO begint met de planning" step shows the real app-wide planning deadline when set,
  // so the Overzicht stays in sync with the Controleren/aanlever copy (falls back when unset).
  const deadlineNice = formatDutchDate(planningDeadline);

  const subText = (i: number): string => {
    if (i === 0) return gegevensAttention ? "Wijzigingen niet aangeleverd" : gegevensIn ? "Aangeleverd" : i === currentIndex ? "Nu invullen" : "Nog nodig";
    if (i === 1) {
      // Delivered-but-incomplete reads as "Nog niet compleet" (a vaste voorstelling of opt-out
      // ontbreekt nog voor een groep); a pending change reads as "Wijzigingen niet aangeleverd".
      if (programmaIncomplete && !p.hasUnsubmittedChanges) return "Nog niet compleet";
      if (programmaAttention) return "Wijzigingen niet aangeleverd";
      return programmaIn ? "Gekozen" : i === currentIndex ? "Nu kiezen" : "Daarna";
    }
    if (i === 2) return i === currentIndex && planningStarted ? "KUVO is bezig" : deadlineNice || "Nog te plannen";
    return "rond 7 juli";
  };

  return (
    <section aria-labelledby="phase-title">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 id="phase-title" className="text-base font-semibold text-foreground">Waar staat u nu?</h2>
        {/* Quiet deadline reminder while aanleveren nog loopt, in sync met de Controleren-copy. */}
        {deadlineNice && currentIndex < 2 && (
          <p className="text-[13px] font-medium text-muted-foreground">
            Lever aan voor {deadlineNice}
          </p>
        )}
      </div>
      <div className="rounded-lg border bg-card px-5 py-7 shadow-xs">
      <ol className="flex">
        {JOURNEY.map((step, i) => {
          const attention = (i === 0 && gegevensAttention) || (i === 1 && programmaAttention);
          const done = i < currentIndex && !attention;
          // The planning step (i === 2) only becomes "current" (KUVO is bezig) once the admin-
          // configured planning date has arrived, not merely because the school submitted everything.
          // Until then it stays a future milestone showing that date.
          const current = i === currentIndex && !attention && (i !== 2 || planningStarted);
          const isLast = i === JOURNEY.length - 1;
          return (
            <li key={step.key} className="relative flex min-w-0 flex-1 flex-col items-center text-center">
              {!isLast && (
                <span className={`absolute left-1/2 top-3.5 h-px w-full ${i < currentIndex ? "bg-primary/40" : "bg-border"}`} aria-hidden="true" />
              )}
              <span
                aria-hidden="true"
                className={`relative z-10 flex size-7 items-center justify-center rounded-full text-[11px] font-semibold motion-safe:transition-colors ${
                  attention
                    ? "border border-amber-300 bg-amber-50 text-amber-700 ring-4 ring-amber-100/70"
                    : done
                      ? "bg-emerald-600 text-white"
                      : current
                        ? "border-2 border-primary bg-background text-primary ring-4 ring-primary/10"
                        : "border border-border bg-background text-muted-foreground"
                }`}
              >
                {attention ? <PencilIcon size={13} /> : done ? <CheckIcon size={14} /> : current ? <span className="size-2 rounded-full bg-primary" /> : i + 1}
              </span>
              <span
                className={`mt-1.5 px-1 text-[12px] leading-tight ${
                  attention ? "font-semibold text-amber-900" : current ? "font-semibold text-foreground" : done ? "font-medium text-foreground" : "font-medium text-muted-foreground"
                }`}
              >
                {step.label}
              </span>
              {i === 0 || i === 1 ? (
                // Gegevens/Programma steps link into their flow; the shell routes to the next
                // unfilled section (or Controleren once everything is complete).
                <Link
                  to={i === 0 ? `/school/${schoolId}/schoolgegevens` : `/school/${schoolId}/programmaselectie`}
                  className={`mt-0.5 text-[11px] font-semibold leading-tight underline-offset-2 hover:underline ${
                    attention ? "text-amber-700 hover:text-amber-800" : current ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {subText(i)}
                </Link>
              ) : (
                <span className={`mt-0.5 text-[11px] leading-tight ${current ? "font-medium text-primary" : "text-muted-foreground"}`}>
                  {subText(i)}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      </div>
    </section>
  );
}